// Pure helpers that keep Explore's world lists in step with a shared room (docs/MULTIPLAYER.md).
// Lists always change in place (splice/push): DiscoveryStream holds `blocks` by reference.
import { B } from './blocks.js';
import { blockItem } from './crafting.js';
import { deviceState as newDeviceState } from './expansion-rules.js';

export const SHARED_DEVICES = new Set([B.DOOR, B.SWITCH, B.DRAWBRIDGE]);
const KINDS = ['place', 'clear', 'mine', 'unmine', 'harvest', 'regrow', 'device'];
const OPS = new Set(['place', 'remove', 'mine', 'harvest', 'device']);
const int = Number.isInteger;
const arr = (v) => (Array.isArray(v) ? v : []);
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const cellOk = (e) => Array.isArray(e) && int(e[0]) && int(e[1]) && int(e[2]);
const entryOk = (e) => cellOk(e) && int(e[3]);
const leftOf = (v) => clamp(Math.round(Number(v)) || 1, 1, 300);
const ownerOf = (o, worldOwner) => (o == null || o === worldOwner ? null : String(o));
const validOp = (op) =>
  !!op && OPS.has(op.k) && int(op.x) && int(op.y) && int(op.z) && (op.k === 'remove' || op.k === 'device' || int(op.id));

export function cellKey(x, y, z) {
  if (Array.isArray(x)) [x, y, z] = x;
  else if (x && typeof x === 'object') ({ x, y, z } = x);
  return `${x},${y},${z}`;
}
export const emptyChanges = () => ({ place: [], clear: [], mine: [], unmine: [], harvest: [], regrow: [], device: [] });
export const hasChanges = (c) => !!c && KINDS.some((k) => c[k]?.length > 0);

const byKey = (list) => new Map(arr(list).filter(cellOk).map((e) => [cellKey(e), e]));
function indexOf(list) {
  const m = new Map();
  list.forEach((e, i) => m.set(cellKey(e), i));
  return m;
}
function compact(list, keys) {
  if (!keys.size) return;
  let w = 0;
  for (const e of list) if (!keys.has(cellKey(e))) list[w++] = e;
  list.length = w;
}

// Snapshot rows as maps; `left` seconds become expiries on the local worldTime clock.
function readWorld(world, worldOwner, worldTime) {
  const w = world && typeof world === 'object' ? world : {};
  const s = { cells: new Map(), removed: new Map(), harvested: new Map(), devices: new Map() };
  for (const e of arr(w.cells)) if (entryOk(e)) s.cells.set(cellKey(e), { b: e.slice(0, 4), owner: ownerOf(e[4], worldOwner) });
  for (const e of arr(w.removed)) if (entryOk(e)) s.removed.set(cellKey(e), e.slice(0, 4));
  for (const e of arr(w.harvested)) if (entryOk(e)) s.harvested.set(cellKey(e), [...e.slice(0, 4), worldTime + leftOf(e[4])]);
  for (const e of arr(w.devices))
    if (cellOk(e) && SHARED_DEVICES.has(s.cells.get(cellKey(e))?.b[3])) s.devices.set(cellKey(e), !!e[3]);
  return s;
}
function toLists(s) {
  const lists = { blocks: [], owners: {}, removed: [...s.removed.values()], harvested: [...s.harvested.values()], devices: {} };
  for (const [k, c] of s.cells) {
    lists.blocks.push(c.b);
    if (c.owner !== null) lists.owners[k] = c.owner;
  }
  const d = { expansion: { devices: lists.devices } };
  for (const [k, on] of s.devices) newDeviceState(d, s.cells.get(k).b).on = on;
  return lists;
}

export function listsFromSnapshot(world, { worldTime = 0, worldOwner = null } = {}) {
  return toLists(readWorld(world, worldOwner, worldTime));
}

// Replays pending ops on the snapshot with the room's rules. `kept` ops still change the room and must be
// sent; `dropped` ops conflict (undo their items); `skipped` ops are already true or cancel out.
export function targetFrom(world, pending, { me = null, worldOwner = null, worldTime = 0 } = {}) {
  const s = readWorld(world, worldOwner, worldTime);
  const boss = me === worldOwner, actor = boss ? null : me;
  const kept = [], dropped = [], skipped = [];
  const placed = new Map(), phantom = new Map();
  const forget = (list, op) => {
    const i = list.indexOf(op);
    if (i >= 0) list.splice(i, 1);
    return i;
  };
  for (const op of arr(pending)) {
    if (!validOp(op)) {
      skipped.push(op);
      continue;
    }
    const k = cellKey(op), cell = s.cells.get(k);
    if (op.k === 'place') {
      if (!cell) {
        s.cells.set(k, { b: [op.x, op.y, op.z, op.id], owner: actor });
        s.devices.delete(k);
        placed.set(k, op);
        phantom.delete(k);
        kept.push(op);
      } else if (cell.b[3] === op.id && cell.owner === actor) skipped.push(op);
      else {
        dropped.push(op);
        phantom.set(k, op);
      }
    } else if (op.k === 'remove') {
      if (phantom.has(k)) {
        // Took down a block that never reached the room: the pair cancels out.
        const p = phantom.get(k);
        forget(dropped, p);
        phantom.delete(k);
        skipped.push(p, op);
      } else if (placed.has(k)) {
        // Placed and taken down again while away: send neither (nor its switch flips).
        const p = placed.get(k), i = forget(kept, p);
        placed.delete(k);
        for (let j = kept.length - 1; j >= Math.max(0, i); j--) if (kept[j].k === 'device' && cellKey(kept[j]) === k) skipped.push(...kept.splice(j, 1));
        s.cells.delete(k);
        s.devices.delete(k);
        skipped.push(p, op);
      } else if (!cell) skipped.push(op);
      // Like the room: a remove for a block that has since been replaced leaves the new block alone.
      else if (op.id != null && cell.b[3] !== op.id) skipped.push(op);
      else if (!boss && cell.owner !== actor) dropped.push(op);
      else {
        s.cells.delete(k);
        s.devices.delete(k);
        kept.push(op);
      }
    } else if (op.k === 'mine') {
      if (cell) dropped.push(op);
      else if (s.removed.has(k)) skipped.push(op);
      else {
        s.removed.set(k, [op.x, op.y, op.z, op.id]);
        kept.push(op);
      }
    } else if (op.k === 'harvest') {
      const until = worldTime + clamp(Math.round(Number.isFinite(op.secs) ? op.secs : 240), 5, 300), old = s.harvested.get(k);
      if (old && old[4] >= until) skipped.push(op);
      else {
        s.harvested.set(k, [op.x, op.y, op.z, op.id, until]);
        kept.push(op);
      }
    } else if (phantom.has(k)) skipped.push(op);
    else if (!SHARED_DEVICES.has(cell?.b[3])) dropped.push(op);
    else if ((s.devices.get(k) ?? false) === !!op.on) skipped.push(op);
    else {
      s.devices.set(k, !!op.on);
      kept.push(op);
    }
  }
  const target = toLists(s);
  target.worldTime = worldTime;
  return { target, kept, dropped, skipped };
}

// Cells present on both sides keep their local harvest expiry, so snapshots don't churn regrowth.
export function diffLists(lists, target, { worldTime = target?.worldTime ?? 0 } = {}) {
  const out = emptyChanges();
  const lo = lists.owners || {}, to = target.owners || {}, ld = lists.devices || {}, td = target.devices || {};
  const local = byKey(lists.blocks), want = byKey(target.blocks);
  for (const [k, b] of local) if (!want.has(k)) out.clear.push(b.slice(0, 4));
  for (const [k, b] of want) {
    const l = local.get(k), owner = to[k] ?? null, same = !!l && l[3] === b[3];
    if (!same || (lo[k] ?? null) !== owner) out.place.push([b[0], b[1], b[2], b[3], owner]);
    const on = !!td[k]?.on;
    if (SHARED_DEVICES.has(b[3]) && on !== (same && !!ld[k]?.on)) out.device.push([b[0], b[1], b[2], on ? 1 : 0]);
  }
  const lr = byKey(lists.removed), tr = byKey(target.removed);
  for (const [k, e] of tr) if (lr.get(k)?.[3] !== e[3]) out.mine.push(e.slice(0, 4));
  for (const [k, e] of lr) if (!tr.has(k)) out.unmine.push(e.slice(0, 4));
  const lh = byKey(lists.harvested), th = byKey(target.harvested);
  for (const [k, e] of th) if (!lh.has(k)) out.harvest.push([...e.slice(0, 4), leftOf(e[4] - worldTime)]);
  for (const [k, e] of lh) if (!th.has(k)) out.regrow.push(e.slice(0, 4));
  return out;
}

// A clear only takes a block with the same id, so a late rollback never removes someone else's newer block.
export function applyChangesToLists(lists, changes, { worldTime = 0, worldOwner = null, deviceState } = {}) {
  const out = emptyChanges();
  if (!lists || !changes) return out;
  const blocks = (lists.blocks ||= []), removed = (lists.removed ||= []), harvested = (lists.harvested ||= []);
  const owners = (lists.owners ||= {}), devices = (lists.devices ||= {});
  let at = indexOf(blocks);
  const gone = new Set();
  for (const e of arr(changes.clear)) {
    const k = cellOk(e) && cellKey(e), i = at.get(k);
    if (i === undefined || gone.has(k) || (int(e[3]) && blocks[i][3] !== e[3])) continue;
    gone.add(k);
    out.clear.push(blocks[i].slice(0, 4));
    delete owners[k];
    delete devices[k];
  }
  if (gone.size) {
    compact(blocks, gone);
    at = indexOf(blocks);
  }
  for (const e of arr(changes.place)) {
    if (!entryOk(e)) continue;
    const k = cellKey(e), owner = ownerOf(e[4], worldOwner), i = at.get(k), b = e.slice(0, 4);
    if (i === undefined) {
      at.set(k, blocks.length);
      blocks.push(b);
    } else if (blocks[i][3] === b[3] && (owners[k] ?? null) === owner) continue;
    else {
      if (blocks[i][3] !== b[3]) delete devices[k];
      blocks[i] = b;
    }
    if (owner === null) delete owners[k];
    else owners[k] = owner;
    out.place.push([...b, owner]);
  }
  let rAt = indexOf(removed);
  const unmined = new Set();
  for (const e of arr(changes.unmine)) {
    const k = cellOk(e) && cellKey(e);
    if (!rAt.has(k) || unmined.has(k)) continue;
    unmined.add(k);
    out.unmine.push(removed[rAt.get(k)].slice(0, 4));
  }
  if (unmined.size) {
    compact(removed, unmined);
    rAt = indexOf(removed);
  }
  for (const e of arr(changes.mine)) {
    if (!entryOk(e)) continue;
    const k = cellKey(e), i = rAt.get(k), r = e.slice(0, 4);
    if (i === undefined) {
      rAt.set(k, removed.length);
      removed.push(r);
    } else if (removed[i][3] === r[3]) continue;
    else removed[i] = r;
    out.mine.push(r.slice());
  }
  let hAt = indexOf(harvested);
  const regrown = new Set();
  for (const e of arr(changes.regrow)) {
    const k = cellOk(e) && cellKey(e);
    if (!hAt.has(k) || regrown.has(k)) continue;
    regrown.add(k);
    out.regrow.push(harvested[hAt.get(k)].slice(0, 4));
  }
  if (regrown.size) {
    compact(harvested, regrown);
    hAt = indexOf(harvested);
  }
  for (const e of arr(changes.harvest)) {
    if (!entryOk(e)) continue;
    const k = cellKey(e), left = leftOf(e[4]), until = worldTime + left, i = hAt.get(k);
    if (i !== undefined) {
      // Already gone here: just never regrow before the room does (cleanHarvested allows 300 s).
      if (harvested[i][4] < until) harvested[i][4] = until;
      continue;
    }
    hAt.set(k, harvested.length);
    harvested.push([e[0], e[1], e[2], e[3], until]);
    out.harvest.push([e[0], e[1], e[2], e[3], left]);
  }
  const make = deviceState || ((k, b) => newDeviceState({ expansion: { devices } }, b));
  for (const e of arr(changes.device)) {
    const k = cellOk(e) && cellKey(e), b = blocks[at.get(k)];
    if (!b || !SHARED_DEVICES.has(b[3])) continue;
    const st = devices[k] || (devices[k] = make(k, b) || {}), on = !!e[3];
    if (!!st.on === on) continue;
    st.on = on;
    out.device.push([e[0], e[1], e[2], on ? 1 : 0]);
  }
  return out;
}

// A `cells` frame from the room as canonical changes (malformed rows are ignored).
export function deltaToChanges(ch) {
  const out = emptyChanges();
  if (!ch || typeof ch !== 'object') return out;
  for (const e of arr(ch.place)) if (entryOk(e)) out.place.push([...e.slice(0, 4), e[4] ?? null]);
  for (const e of arr(ch.clear)) if (cellOk(e)) out.clear.push(e.slice(0, int(e[3]) ? 4 : 3));
  for (const k of ['mine', 'unmine', 'regrow']) for (const e of arr(ch[k])) if (entryOk(e)) out[k].push(e.slice(0, 4));
  for (const e of arr(ch.harvest)) if (entryOk(e)) out.harvest.push([...e.slice(0, 4), leftOf(e[4])]);
  for (const e of arr(ch.device)) if (cellOk(e)) out.device.push([e[0], e[1], e[2], e[3] ? 1 : 0]);
  return out;
}

export function inverseOf(op) {
  const c = emptyChanges();
  if (!op) return c;
  const { x, y, z, id } = op;
  if (op.k === 'place') c.clear.push([x, y, z, id]);
  else if (op.k === 'remove' && int(id)) c.place.push([x, y, z, id, op.prevOwner ?? null]);
  else if (op.k === 'mine') c.unmine.push([x, y, z, id]);
  else if (op.k === 'harvest') c.regrow.push([x, y, z, id]);
  else if (op.k === 'device') c.device.push([x, y, z, (op.prevOn ?? !op.on) ? 1 : 0]);
  return c;
}

// Placed here by `me` (the world owner owns every cell without an `owners` entry).
export function isMine(lists, cell, me, worldOwner) {
  const k = cellKey(cell);
  if (!arr(lists?.blocks).some((b) => cellKey(b) === k)) return false;
  return (lists.owners?.[k] ?? worldOwner) === me;
}

// `owners` is never saved, so your own world's save marks each block a friend built with a 1 after its id
// ([x,y,z,id,1]). Until a snapshot says who built it, such a block belongs to FRIEND: never you, so taking
// it down gives no item, and seeding never copies it as yours.
export const FRIEND = 'friend';
export function friendMarks(blocks) {
  const owners = {};
  for (const b of arr(blocks)) if (cellOk(b) && b[4] === 1) owners[cellKey(b)] = FRIEND;
  return owners;
}
export function markFriends(blocks, owners) {
  for (const b of arr(blocks))
    if (owners?.[cellKey(b)] != null) b[4] = 1;
    else if (b.length > 4) b.length = 4;
}

// Blocks taken down while your own world's edit queue was full (`discovery.takenDown`, saved as
// [x,y,z,id] or [x,y,z,id,1] for a friend's block). They are replayed before anything else.
export function takenDownOps(list) {
  return arr(list)
    .filter(entryOk)
    .map((t) => {
      const friend = t[4] === 1, item = blockItem(t[3]);
      return { k: 'remove', x: t[0], y: t[1], z: t[2], id: t[3], item: friend ? null : item || null, n: friend || !item ? 0 : 1, prevOwner: friend ? FRIEND : null, tomb: t };
    });
}

// First copy of a world made before shared worlds. Mines go first: the room refuses mining a placed cell.
export function seedOps(lists, worldTime = 0, { includeOff = false } = {}) {
  const owners = lists.owners || {}, devices = lists.devices || {}, ops = [], flips = [];
  for (const [x, y, z, id] of arr(lists.removed).filter(entryOk)) ops.push({ k: 'mine', x, y, z, id });
  for (const [x, y, z, id, until] of arr(lists.harvested).filter(entryOk))
    if (until > worldTime) ops.push({ k: 'harvest', x, y, z, id, secs: clamp(Math.round(until - worldTime), 5, 300) });
  for (const [x, y, z, id] of arr(lists.blocks).filter(entryOk)) {
    const k = cellKey(x, y, z), item = blockItem(id);
    if (owners[k] != null) continue;
    ops.push(item ? { k: 'place', x, y, z, id, item, n: 1 } : { k: 'place', x, y, z, id });
    if (SHARED_DEVICES.has(id) && (includeOff || devices[k]?.on)) {
      const on = devices[k]?.on ? 1 : 0;
      flips.push({ k: 'device', x, y, z, on, prevOn: on ? 0 : 1 });
    }
  }
  return [...ops, ...flips];
}

const WORLD_KEYS = new Set(['blocks', 'removed', 'harvested', 'owners']);
const SHARED_EXPANSION = new Set(['devices', 'home', 'animals']);
const has = (o, p) => !!o && Object.prototype.hasOwnProperty.call(o, p);
const field = (value) => ({ value, writable: true, enumerable: true, configurable: true });

// A visit: the guest's own discovery, with the friend's world lists and shared expansion state routed to `world`.
export function sessionDiscovery(own, world) {
  for (const k of ['blocks', 'removed', 'harvested']) if (!Array.isArray(world[k])) world[k] = [];
  world.owners ||= {};
  const ex = (world.expansion ||= {});
  ex.devices ||= world.devices || {};
  ex.home ??= null;
  ex.animals ||= [];
  const ownEx = () => (own.expansion && typeof own.expansion === 'object' ? own.expansion : null);
  const get = (p) => (SHARED_EXPANSION.has(p) ? world.expansion[p] : ownEx()?.[p]);
  const view = new Proxy({}, {
    get: (_, p) => get(p),
    set: (_, p, v) => {
      if (SHARED_EXPANSION.has(p)) world.expansion[p] = v;
      else (ownEx() || (own.expansion = {}))[p] = v;
      return true;
    },
    has: (_, p) => SHARED_EXPANSION.has(p) || (!!ownEx() && p in ownEx()),
    deleteProperty: (_, p) => (SHARED_EXPANSION.has(p) ? delete world.expansion[p] : !ownEx() || delete ownEx()[p]),
    ownKeys: () => [...new Set([...Reflect.ownKeys(ownEx() || {}), ...SHARED_EXPANSION])],
    getOwnPropertyDescriptor: (_, p) => (SHARED_EXPANSION.has(p) || has(ownEx(), p) ? field(get(p)) : undefined),
  });
  // ensureExpansion assigns a whole new object: the world's part goes to the world, the rest to the player.
  const split = (v) => {
    const next = v && typeof v === 'object' ? v : {}, prev = ownEx(), mine = {};
    for (const k of Object.keys(next)) if (SHARED_EXPANSION.has(k)) world.expansion[k] = next[k];
    else mine[k] = next[k];
    for (const k of SHARED_EXPANSION) if (has(prev, k)) mine[k] = prev[k];
    own.expansion = mine;
    if ('devices' in world) world.devices = world.expansion.devices;
  };
  return new Proxy(own, {
    get: (t, p) => (WORLD_KEYS.has(p) ? world[p] : p === 'expansion' ? view : Reflect.get(t, p)),
    set: (t, p, v) => {
      if (WORLD_KEYS.has(p)) world[p] = v;
      else if (p === 'expansion') split(v);
      else t[p] = v;
      return true;
    },
    has: (t, p) => WORLD_KEYS.has(p) || p === 'expansion' || p in t,
    deleteProperty: (t, p) => (WORLD_KEYS.has(p) ? delete world[p] : p === 'expansion' || delete t[p]),
    ownKeys: (t) => [...new Set([...Reflect.ownKeys(t), ...WORLD_KEYS, 'expansion'])],
    getOwnPropertyDescriptor: (t, p) =>
      WORLD_KEYS.has(p) ? field(world[p]) : p === 'expansion' ? field(view) : Reflect.getOwnPropertyDescriptor(t, p),
    defineProperty: (t, p, desc) => {
      if (WORLD_KEYS.has(p)) world[p] = desc.value;
      else if (p === 'expansion') split(desc.value);
      else return Reflect.defineProperty(t, p, desc);
      return true;
    },
  });
}
